import { yupResolver } from '@hookform/resolvers/yup';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Button, FormGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import AvatarInput from 'v2/form/AvatarInput/AvatarInput';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import Input from 'v2/form/Input/Input';
import RichTextEditor from 'v2/form/RichTextEditor/RichTextEditor';
import TextArea from 'v2/form/TextArea/TextArea';
import { GenericObject, User } from 'v2/models';
import * as yup from 'yup';
import { updateUserProfileAction } from '../../../../actions';
import avatarProvider from '../../../images/avatarProvider.png';
import styles from '../settingsForm.module.scss';

const getSchema = (showVagueDescription: boolean) =>
  yup.object().shape({
    orgName: yup.string(),
    classification: yup.string(),
    shortDescription: yup.string().required('Required'),
    longDescription: yup.string().required('Required'),
    ...(showVagueDescription
      ? { vagueDescription: yup.string().required('Required') }
      : {}),
  });

interface ModelToSave {
  short_description: string;
  long_description: string;
  vague_description: string;
  avatar: File | string | null;
}

interface StateProps {
  data: {
    orgName: string;
    classification: string;
    shortDescription: string;
    longDescription: string;
    vagueDescription: string;
    avatar: { id: number; image: string | File };
  };
}

interface DispatchProps {
  // Need to use Funtion type here because coulnd't find the proper type definition for
  // updateUserProfileAction action and make it work with the connect redux function
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateUserProfileAction: Function;
}

interface MyOrganizationOwnProps {
  isDataProvider: boolean;
  isAdmin: boolean;
  showVagueDescription?: boolean;
}

type MyOrganizationProps = StateProps & DispatchProps & MyOrganizationOwnProps;

const MyOrganization = ({
  data,
  updateUserProfileAction,
  isDataProvider,
  isAdmin,
  showVagueDescription = true,
}: MyOrganizationProps) => {
  const schema = getSchema(showVagueDescription);
  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    setValue,
    getValues,
    reset,
  } = useForm<StateProps['data']>({
    defaultValues: data || {
      orgName: '',
      classification: '',
      shortDescription: '',
      longDescription: '',
      vagueDescription: '',
      avatar: {
        id: 0,
        image: '',
      },
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const formValues = getValues();

  useEffect(() => {
    register('avatar');
  }, [register]);

  const onSubmit = async (values: StateProps['data']) => {
    try {
      let dataToSave: Partial<ModelToSave> = {
        short_description: values.shortDescription,
        long_description: values.longDescription,
        ...(showVagueDescription
          ? { vague_description: values.vagueDescription }
          : {}),
      };
      // We identify if there is a new avatar with id == 0. When there is no ID is because there is no avatar
      if (values.avatar.id === 0) {
        dataToSave = { ...dataToSave, avatar: values.avatar.image || null };
      }
      setIsBusy(true);
      if (isDirty) {
        const response = await updateUserProfileAction({
          [isDataProvider ? 'dp_updates' : 'org_updates']: dataToSave,
        });
        reset(
          mapOrganizationSettings(
            isDataProvider ? response.data_provider : response.organization
          )
        );
        toast.success('Account updated.');
      }
      setIsEditing(false);
      setIsBusy(false);
    } catch (error) {
      toast.error(String(error));
      setIsEditing(false);
      setIsBusy(false);
    }
  };

  const renderActions = () => {
    return (
      <>
        {!isEditing && (!isDataProvider || isAdmin) ? (
          <Button
            type="button"
            className="px-5 mt-5"
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              setIsEditing(true);
            }}
            disabled={isBusy}
          >
            Edit details
          </Button>
        ) : null}
        {isEditing ? (
          <Button
            className="px-5 mt-5"
            size="sm"
            type="submit"
            disabled={isBusy}
          >
            {isBusy ? 'Loading...' : 'Save changes'}
          </Button>
        ) : null}
      </>
    );
  };

  return (
    <>
      <h4 className={classnames('mb-5', styles.title)}>
        Organization Information
      </h4>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AvatarInput
          className={classnames('form-group', styles.avatar)}
          file={formValues.avatar.image || ''}
          label="Organization Logo"
          onChange={(data) => {
            setValue(
              'avatar',
              { id: 0, image: data || '' },
              { shouldValidate: true, shouldDirty: true }
            );
          }}
          readonly={!isEditing}
          changeBtnText="Edit"
          rounded={false}
        />
        <div className={classnames(styles.formGrid, 'mt-5 mb-4')}>
          <FormGroup>
            <Input
              label="Organization Name"
              type="text"
              register={register('orgName')}
              required={false}
              disabled={true}
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="Organization Type"
              type="text"
              register={register('classification', { required: true })}
              required={false}
              disabled={true}
            />
          </FormGroup>
        </div>
        <FormGroup>
          <TextArea
            label="Organization Short Description"
            register={register('shortDescription', { required: true })}
            error={errors?.shortDescription?.message}
            rows={4}
            autoComplete="off"
            required={true}
            disabled={!isEditing}
          />
        </FormGroup>
        <FormGroup className="mt-5">
          <FormLabel
            label="Organization Detailed Description"
            required={true}
            error={errors?.longDescription?.message}
          />
          <RichTextEditor
            readOnly={!isEditing}
            value={formValues.longDescription || ''}
            onChange={(data) =>
              setValue('longDescription', data, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </FormGroup>
        {showVagueDescription ? (
          <FormGroup className="mt-5">
            <TextArea
              label="Organization Hidden Identity Description"
              register={register('vagueDescription', { required: true })}
              error={errors?.vagueDescription?.message}
              rows={6}
              autoComplete="off"
              required={true}
              disabled={!isEditing}
            />
          </FormGroup>
        ) : null}
        {renderActions()}
      </form>
    </>
  );
};

const mapStateToProps = (state: GenericObject): StateProps => {
  return {
    data: mapOrganizationSettings(
      state.user.user.organization?.id
        ? state.user.user.organization
        : state.user.user.data_provider
    ),
  };
};

export default connect<StateProps, DispatchProps, MyOrganizationOwnProps>(
  mapStateToProps,
  {
    updateUserProfileAction,
  }
)(MyOrganization);

function mapOrganizationSettings(
  organization: User['organization'] | User['data_provider']
) {
  return {
    orgName: organization.name,
    classification: organization.classification || '',
    shortDescription: organization.short_description || '',
    longDescription: organization.long_description || '',
    vagueDescription: organization.vague_description || '',
    avatar: {
      id: organization.avatar?.id,
      image:
        organization.avatar?.image?.formats?.thumbnail?.url ||
        organization.avatar?.image?.url ||
        avatarProvider,
    },
  };
}
